import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import type { PaginatedResponse } from '@/types/api.types'
import type { User } from '@/types/user.types'
import { AdminPage } from '@/pages/AdminPage'
import { fetchUsers } from '@/services/api/users'
import {
  HTTP_GET_FILTERS,
  HTTP_GET_FILTER_DEFAULT_VALUES,
} from '@/constants/apiConstants'
import { requireRoles } from '@/utils/routeGuards.ts'
import { ROUTE_ACCESS } from '@/constants/routeAccess.ts'
import { ROUTES } from '@/constants/routeConstants'

export const userSearchSchema = z.object({
  offset: z.number().nonnegative().default(0).catch(0),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  role: z
    .enum(['admin', 'salesRepresentative', 'consultant', 'client', 'all'])
    .optional(),
  agency: z.string().optional(),
  search: z.string().optional(),
})

export const Route = createFileRoute('/admin/')({
  beforeLoad: requireRoles(ROUTE_ACCESS[ROUTES.ADMIN]),
  validateSearch: userSearchSchema,
  loaderDeps: ({
    search: { offset, sortBy, sortOrder, role, agency, search },
  }) => ({
    offset,
    sortBy,
    sortOrder,
    filters: { role, agency },
    search,
  }),
  loader: async ({
    deps: { offset, sortBy, sortOrder, filters, search },
  }): Promise<{
    users: PaginatedResponse<User>
    error: null | unknown
    isPending?: boolean
  }> => {
    try {
      const users = await fetchUsers({
        offset,
        sortBy,
        sortOrder,
        filters,
        search,
      })

      return { users, error: null, isPending: false }
    } catch (error) {
      return {
        users: {
          total: 0,
          offset: Number(
            HTTP_GET_FILTER_DEFAULT_VALUES[HTTP_GET_FILTERS.OFFSET],
          ),
          limit: Number(HTTP_GET_FILTER_DEFAULT_VALUES[HTTP_GET_FILTERS.LIMIT]),
          totalPages: 0,
          items: [],
        },
        error,
      }
    }
  },
  component: AdminPage,
})
